import { t } from "@hakili/i18n";
import { useCallback, useEffect, useState } from "react";

import type {
  DossierProformas,
  DossierRequest,
  DossierRequestDoors,
  IssuedProforma,
} from "../session/vehicle-papers.js";
import { BOUTON_TEXTE } from "@hakili/ui";
import { ReadFailure } from "./read-failure.js";

const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

/** `1 234 567 FCFA`, the way every screen of the product prints a price. */
const francs = (amount: number): string =>
  `${String(amount).replace(/\B(?=(\d{3})+(?!\d))/gu, "\u202f")}\u00a0FCFA`;

/** `2026-10-17` -> `17/10/2026`. */
const frenchDate = (day: string): string => day.split("-").reverse().join("/");

const lineOf = (one: IssuedProforma): string =>
  fill("dealer.vehicules.emiseLigne", {
    numero: one.numberLabel,
    version: String(one.version),
    date: frenchDate(one.validUntil),
    prix: francs(one.priceTtcAmount),
    ht: francs(one.priceHtAmount),
    tva: francs(one.tvaAmount),
  });

/**
 * `A-DLR`, Arc B (+D): **« Pièces demandées » , what the dealer's dossiers on this car ask of him.**
 *
 * The car's and the file's reference, never the buyer. The carte grise and the visite technique are the VEHICLE's:
 * they are supplied once in « Papiers du véhicule » and fill the file by themselves. The facture proforma is the
 * file's own and is deposited here, through his own door, until it is issued from this space.
 */
export function DossierRequestsCard({
  vehicleId,
  doors,
}: {
  readonly vehicleId: string;
  readonly doors: DossierRequestDoors;
}): React.JSX.Element | null {
  const [requests, setRequests] = useState<readonly DossierRequest[] | null>(null);
  const [issued, setIssued] = useState<readonly DossierProformas[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  /** DEALER-READ-FAILURE-LISTS: the door's message when the READ failed (a write that failed stays in `message`, in the door's own words). */
  const [readFailure, setReadFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const answer = await doors.readRequests(vehicleId);
    if (!answer.ok) {
      setReadFailure(answer.message);
      return;
    }
    setReadFailure(null);
    setRequests(answer.value.requests);
    setIssued(answer.value.issued);
  }, [doors, vehicleId]);

  useEffect(() => {
    void load();
  }, [load]);

  const choose = async (request: DossierRequest, file: File): Promise<void> => {
    setMessage(null);
    setBusy(request.dossierId);
    const sent = await doors.uploadProforma({ vehicleId, dossierId: request.dossierId, file });
    setBusy(null);
    if (!sent.ok) {
      setMessage(sent.message);
      return;
    }
    await load();
  };

  const issue = async (dossierId: string): Promise<void> => {
    setMessage(null);
    setBusy(dossierId);
    const sent = await doors.issueProforma({ vehicleId, dossierId });
    setBusy(null);
    if (!sent.ok) {
      // The api's own sentence, naming each missing field of his fiche or of the car.
      setMessage(sent.message);
      return;
    }
    await load();
  };

  // DEALER-READ-FAILURE-LISTS: a read that FAILED is said, in the recorded words; the card does not draw the raw refusal over nothing.
  if (readFailure !== null) {
    return (
      <div className="mb-4">
        <ReadFailure
          what="dealer.lecture.pieces"
          message={readFailure}
          onRetry={() => {
            void load();
          }}
          testId="vehicule-echec-demandes"
          retryTestId="vehicule-echec-demandes-reessayer"
        />
      </div>
    );
  }

  // Nothing is asked and nothing issued: the card says nothing , a dealer is not shown an empty obligation.
  if (requests !== null && requests.length === 0 && issued.length === 0 && message === null) return null;

  return (
    <section
      className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3"
      data-testid="pieces-demandees"
    >
      <h3 className="text-sm font-semibold text-gray-900">{t("dealer.vehicules.demandeesTitre")}</h3>
      <ul className="mt-2 space-y-2">
        {(requests ?? []).map((request) => (
          <li
            key={`${request.dossierId}-${request.code}`}
            className="flex flex-wrap items-center gap-2 text-sm"
            data-testid={`demande-${request.code}`}
          >
            <span className="font-medium text-gray-900">{request.label}</span>
            <span className="text-xs text-gray-500">
              {t("dealer.vehicules.demandeesDossier")} {request.reference}
            </span>
            <span className="text-xs text-amber-700" data-testid={`demande-${request.code}-motif`}>
              {request.reason === "expired"
                ? t("dealer.vehicules.demandeesExpiree")
                : request.reason === "rejected"
                  ? `${t("dealer.vehicules.demandeesRefusee")}${request.rejectionReason === null ? "" : ` : ${request.rejectionReason}`}`
                  : t("dealer.vehicules.demandeesManquante")}
            </span>
            {request.where === "vehicule" ? (
              <span className="text-xs text-gray-500" data-testid={`demande-${request.code}-ou`}>
                {t("dealer.vehicules.demandeesDansPapiers")}
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className="rounded bg-gray-900 px-2 py-0.5 text-xs text-white"
                  data-testid={`demande-${request.code}-emettre`}
                  disabled={busy !== null}
                  onClick={() => {
                    void issue(request.dossierId);
                  }}
                >
                  {busy === request.dossierId
                    ? t("dealer.vehicules.demandeesEmission")
                    : t("dealer.vehicules.demandeesEmettre")}
                </button>
                <label className="cursor-pointer rounded border border-gray-300 bg-white px-2 py-0.5 text-xs">
                  {busy === request.dossierId
                    ? t("dealer.vehicules.papierEnvoi")
                    : t("dealer.vehicules.demandeesDeposerSecours")}
                  <input
                    type="file"
                    className="sr-only"
                    accept="image/jpeg,image/png,image/heic,application/pdf"
                    data-testid={`demande-${request.code}-fichier`}
                    disabled={busy !== null}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file !== undefined) void choose(request, file);
                      event.target.value = "";
                    }}
                  />
                </label>
              </>
            )}
          </li>
        ))}
      </ul>
      {issued.length === 0 ? null : (
        <div className="mt-3" data-testid="proformas-emises">
          <h4 className="text-xs font-semibold text-gray-900">{t("dealer.vehicules.emiseTitre")}</h4>
          {issued.map((file) => (
            <div key={file.dossierId} className="mt-1" data-testid={`emises-${file.dossierId}`}>
              <p className="text-xs text-gray-500">
                {t("dealer.vehicules.demandeesDossier")} {file.reference}
              </p>
              <ul className="space-y-1">
                {[...file.versions].reverse().map((one) => (
                  <li
                    key={one.version}
                    className="flex flex-wrap items-center gap-2 text-sm"
                    data-testid={`emise-${String(one.version)}`}
                  >
                    <span className={one.superseded ? "text-gray-500 line-through" : "text-gray-900"}>
                      {lineOf(one)}
                    </span>
                    {one.superseded ? (
                      <span className="text-xs text-gray-500">{t("dealer.vehicules.emiseRemplacee")}</span>
                    ) : null}
                    {one.url === null ? null : (
                      <a
                        href={one.url}
                        target="_blank"
                        rel="noreferrer"
                        className={`text-xs ${BOUTON_TEXTE}`}
                        data-testid={`emise-${String(one.version)}-ouvrir`}
                      >
                        {t("dealer.vehicules.emiseOuvrir")}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
              {file.newVersionOwed ? (
                <button
                  type="button"
                  className="mt-1 rounded bg-gray-900 px-2 py-0.5 text-xs text-white"
                  data-testid={`emises-${file.dossierId}-nouvelle`}
                  disabled={busy !== null}
                  onClick={() => {
                    void issue(file.dossierId);
                  }}
                >
                  {t("dealer.vehicules.emiseNouvelleVersion")}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {message === null ? null : (
        <p className="mt-2 text-xs text-amber-700" role="alert" data-testid="demandes-message">
          {message}
        </p>
      )}
    </section>
  );
}
